import type { User as SupabaseUser } from '@supabase/supabase-js'
import { getSupabaseAuthClient, getSupabaseClient } from '../supabase'
import { devLog } from '../../utilities/dev-log'
import type {
  ActionToolAccountWithId,
  ActionToolUserWithId,
  UserSettings
} from '../../types/account-types'
import {
  applyPrivacySettingsToProfile,
  createAccountRealtimeClient,
  type AccountBillingSummary,
  type AccountNotificationPreferences,
  type AccountNotificationRealtimeEvent,
  createAccountClient,
  createSupabaseAccountAuthClient,
  waitForSelfAccount,
  type AccountClientService,
  type AccountService,
  type AccountVerificationSummary,
  type MagicLinkAuthClient,
  type FriendActivityRealtimeEvent,
  type PublicProfileActivity,
  type PublicProfile,
  type PublicProfileCard,
  type SelfAccount
} from '@important-not-important-projects/wcid-account-core'
import { getDomain } from '../../utilities/browser'

const debugAccountCoreCall = (
  functionName: string,
  metadata?: Record<string, string | number | boolean | null | undefined>
) => {
  devLog('[account-core]', functionName, metadata)
}

type Unsubscribe = () => void

type LegacyTimestampValue = NonNullable<
  NonNullable<ActionToolAccountWithId['stripe_verification']>['modal_date']
>

const createLegacyTimestamp = (value: string): LegacyTimestampValue =>
  ({
    toDate: () => new Date(value)
  }) as LegacyTimestampValue

export type AccountAuthProvider = 'supabase'

export type AccountAuthUser = {
  uid: string
  authUserId: string | null
  email: string | null
  emailVerified: boolean
  provider: AccountAuthProvider
}

type ExtendedAccountService = AccountService & {
  getPublicProfilesByIds(
    accountIds: string[],
    viewerAccountId?: string | null
  ): Promise<PublicProfileCard[]>
}

type ExtendedAccountClientService = AccountClientService & {
  getPublicProfilesByIds(
    accountIds: string[],
    viewerAccountId?: string | null
  ): Promise<PublicProfileCard[]>
}

type SupabaseAccountIdentityResponse = {
  exists: boolean
  accountId?: string | null
  authUserId?: string | null
  legacyFirebaseUid?: string | null
  emailNormalized?: string | null
}

const parseResponseErrorMessage = async (response: Response) => {
  const responseText = await response.text()

  if (!responseText) {
    return `Request failed with status ${response.status}`
  }

  try {
    const parsed = JSON.parse(responseText) as string | { message?: string }
    return typeof parsed === 'string' ? parsed : parsed.message || responseText
  } catch {
    return responseText
  }
}

const ACCOUNT_IDENTITY_PATH = '/.netlify/functions/account-auth-supabase-identity'

/**
 * The signed-in user's own account identity. Authenticated with their session token;
 * the endpoint only ever returns the caller's identity (never another account's).
 */
const fetchSupabaseAccountIdentity = async ({ authUserId }: { authUserId: string }) => {
  debugAccountCoreCall('supabase.fetchSupabaseAccountIdentity.start', { authUserId })

  const session = await ensureSupabaseAuthSessionReady()
  if (!session?.access_token) {
    debugAccountCoreCall('supabase.fetchSupabaseAccountIdentity.noSession', { authUserId })
    return null
  }

  const response = await fetch(ACCOUNT_IDENTITY_PATH, {
    headers: { Authorization: `Bearer ${session.access_token}` }
  })
  const errorMessage = !response.ok ? await parseResponseErrorMessage(response) : null

  if (errorMessage) {
    debugAccountCoreCall('supabase.fetchSupabaseAccountIdentity.error', {
      authUserId,
      status: response.status,
      errorMessage
    })
    throw new Error(errorMessage)
  }

  const body = (await response.json()) as SupabaseAccountIdentityResponse
  debugAccountCoreCall('supabase.fetchSupabaseAccountIdentity.result', {
    authUserId,
    exists: body.exists,
    accountId: body.accountId ?? null,
    legacyFirebaseUid: body.legacyFirebaseUid ?? null
  })
  return body.exists ? body : null
}

const postSupabaseAccountAdminRequest = async <T = void>(
  path: string,
  body: Record<string, unknown>
) => {
  debugAccountCoreCall('supabase.accountAdminRequest.start', {
    path
  })

  const session = await ensureSupabaseAuthSessionReady()
  const headers: Record<string, string> = {
    'Content-Type': 'application/json'
  }

  if (session?.access_token) {
    headers.Authorization = `Bearer ${session.access_token}`
  }

  const response = await fetch(path, {
    method: 'POST',
    headers,
    body: JSON.stringify(body)
  })

  const errorMessage = !response.ok ? await parseResponseErrorMessage(response) : null

  if (errorMessage) {
    debugAccountCoreCall('supabase.accountAdminRequest.error', {
      path,
      status: response.status,
      errorMessage
    })
    throw new Error(errorMessage)
  }

  const responseText = await response.text()

  debugAccountCoreCall('supabase.accountAdminRequest.result', {
    path,
    status: response.status,
    hasBody: Boolean(responseText)
  })

  return responseText ? (JSON.parse(responseText) as T) : undefined
}

const ensureSupabaseAuthSessionReady = async () => {
  const supabase = getSupabaseAuthClient()

  if (!supabase) {
    return null
  }

  const { data, error } = await supabase.auth.getSession()

  if (error) {
    debugAccountCoreCall('supabase.ensureSession.error', {
      message: error.message,
      status: typeof (error as { status?: number }).status === 'number' ? error.status : null
    })
    throw error
  }

  debugAccountCoreCall('supabase.ensureSession.result', {
    hasSession: Boolean(data.session),
    authUserId: data.session?.user?.id ?? null,
    email: data.session?.user?.email ?? null
  })

  return data.session
}

const normalizeSupabaseAuthUser = async (
  user?: SupabaseUser | null
): Promise<AccountAuthUser | null> => {
  if (!user?.id) {
    return null
  }

  const identity = await fetchSupabaseAccountIdentity({ authUserId: user.id })

  return {
    // TODO: When account unification is complete, stop remapping to legacy account ids and use the Supabase auth user id as the canonical account id end-to-end.
    uid: identity?.legacyFirebaseUid || identity?.accountId || user.id,
    authUserId: user.id,
    email: user.email ?? null,
    emailVerified:
      Boolean((user as { email_confirmed_at?: string | null }).email_confirmed_at) ||
      Boolean(identity),
    provider: 'supabase'
  }
}

let currentAccountAuthUser: AccountAuthUser | null = null

let supabaseAccountAuth: MagicLinkAuthClient<SupabaseUser> | null | undefined

const getSupabaseAccountAuth = () => {
  if (supabaseAccountAuth !== undefined) {
    return supabaseAccountAuth
  }

  const supabase = getSupabaseAuthClient()

  if (!supabase) {
    supabaseAccountAuth = null
    return supabaseAccountAuth
  }

  supabaseAccountAuth = createSupabaseAccountAuthClient<SupabaseUser>({
    authApi: {
      getSession: async () => {
        const { data, error } = await supabase.auth.getSession()
        return {
          error: error
            ? {
                code: typeof (error as { code?: string }).code === 'string' ? error.code : null,
                message: error.message,
                status:
                  typeof (error as { status?: number }).status === 'number' ? error.status : null
              }
            : null,
          session: data.session ? { user: data.session.user } : null
        }
      },
      onAuthStateChange: next => {
        const {
          data: { subscription }
        } = supabase.auth.onAuthStateChange((event, session) =>
          next(event, session ? { user: session.user } : null)
        )

        return {
          unsubscribe: () => subscription.unsubscribe()
        }
      },
      signOut: async () => {
        const { error } = await supabase.auth.signOut()
        return {
          error: error
            ? {
                code: typeof (error as { code?: string }).code === 'string' ? error.code : null,
                message: error.message,
                status:
                  typeof (error as { status?: number }).status === 'number' ? error.status : null
              }
            : null
        }
      },
      verifyOtp: async ({ tokenHash, type }) => {
        // Tokens minted by admin generateLink({ type: 'magiclink' }) carry a
        // hashed_token whose verification_type is reported as 'magiclink', but
        // GoTrue's token_hash verify path expects 'email' for these email OTP
        // hashes. Passing 'magiclink' through is rejected as otp_expired even for
        // a fresh, valid token. Force 'email' (fall back to the given type only
        // for non-magiclink flows like recovery/invite/email_change).
        const verifyType = !type || type === 'magiclink' ? 'email' : (type as 'email')
        const { data, error } = await supabase.auth.verifyOtp({
          token_hash: tokenHash,
          type: verifyType
        })

        return {
          error: error
            ? {
                code: typeof (error as { code?: string }).code === 'string' ? error.code : null,
                message: error.message,
                status:
                  typeof (error as { status?: number }).status === 'number' ? error.status : null
              }
            : null,
          session: data.session ? { user: data.session.user } : null,
          user: data.user ?? null
        }
      }
    },
    buildMagicLinkUrl: (email, redirectPath) => {
      const params = new URLSearchParams({ email })
      const domain = getDomain()
      const targetUrl = domain ? `${domain}${redirectPath}` : redirectPath

      if (targetUrl) {
        params.set('redirectUrl', targetUrl)
      }

      return `/.netlify/functions/email-login-link-supabase?${params.toString()}`
    },
    logger: (event, metadata) => debugAccountCoreCall(`supabase.${event}`, metadata),
    notify: message => {
      if (typeof window === 'undefined') {
        return
      }

      window.alert(message)
    }
  })

  return supabaseAccountAuth
}

export const getCurrentAuthUser = () => {
  debugAccountCoreCall('getCurrentAuthUser')
  return currentAccountAuthUser
}

export const handleMagicLinkRedirectUrl = async () => {
  debugAccountCoreCall('handleMagicLinkRedirectUrl')
  const supabaseAuth = getSupabaseAccountAuth()

  if (!supabaseAuth) {
    throw new Error('Supabase auth client is unavailable')
  }

  try {
    const handled = await supabaseAuth.handleMagicLinkRedirectUrl()
    currentAccountAuthUser = await normalizeSupabaseAuthUser(supabaseAuth.getCurrentUser())
    debugAccountCoreCall('supabase.handleMagicLinkRedirectUrl.result', {
      handled: Boolean(handled),
      authUserId: currentAccountAuthUser?.authUserId ?? null,
      uid: currentAccountAuthUser?.uid ?? null,
      email: currentAccountAuthUser?.email ?? null
    })
    return handled
  } catch (error) {
    debugAccountCoreCall('supabase.handleMagicLinkRedirectUrl.error', {
      message: error instanceof Error ? error.message : 'unknown'
    })
    throw error
  }
}

export const isMagicLinkRedirectUrl = () => {
  debugAccountCoreCall('isMagicLinkRedirectUrl')
  return Boolean(getSupabaseAccountAuth()?.isMagicLinkRedirectUrl())
}

export const observeAuthState = (next: (user: AccountAuthUser | null) => void): Unsubscribe => {
  debugAccountCoreCall('observeAuthState')
  const supabaseAuth = getSupabaseAccountAuth()

  if (!supabaseAuth) {
    throw new Error('Supabase auth client is unavailable')
  }

  return supabaseAuth.observeAuthState(rawUser => {
    void normalizeSupabaseAuthUser(rawUser).then(user => {
      currentAccountAuthUser = user
      next(user)
    })
  })
}

// ─── Email code sign-in ──────────────────────────────────────────────────────
// Code-only sign-in: the email carries a 6-digit one-time code (no link), which the
// user types into the app. Works on the device they're on — including the installed
// PWA, where a magic link would open the browser instead.

/**
 * Dev mode for the code flow (GATSBY_EMAIL_LOGIN_CODE_DEV_MODE=true): ask the server
 * not to send the email and to return the code, so the flow can be tested end to
 * end. The server only honors it when EMAIL_LOGIN_CODE_DEV_MODE=true there too.
 */
const isEmailLoginCodeDevMode = () => process.env.GATSBY_EMAIL_LOGIN_CODE_DEV_MODE === 'true'

export const EMAIL_LOGIN_CODE_LENGTH = 6

/**
 * Thrown when the server needs a Turnstile CAPTCHA before it will continue (the
 * email or IP is under pressure). Retry the same call with a `captchaToken`.
 */
export class CaptchaRequiredError extends Error {
  constructor(message = 'Please complete the verification to continue.') {
    super(message)
    this.name = 'CaptchaRequiredError'
  }
}

type CodeRequestOptions = {
  /** The challenge from a previous request in this browser (a resend reuses it) */
  challengeId?: string | null
  /** A Turnstile token, when the server asked for one */
  captchaToken?: string | null
}

// Turns a failed response into the right error, flagging CAPTCHA requests
const throwForResponse = async (response: Response, fallback: string): Promise<never> => {
  const text = await response.text()
  let body: { message?: string; captchaRequired?: boolean } | string | undefined
  try {
    body = text ? JSON.parse(text) : undefined
  } catch {
    body = text
  }
  const message = typeof body === 'string' ? body : body?.message
  if (typeof body === 'object' && body?.captchaRequired) {
    throw new CaptchaRequiredError(message)
  }
  throw new Error(message || fallback)
}

/**
 * Requests a code for signing in (`login`) or creating an account (`register`). The
 * response is the same whether or not the email has an account — the server decides
 * which email to send — and carries the challenge id that must accompany the code.
 *
 * Dev mode only: `devCode` is the code (null when no usable code was issued, e.g.
 * signing in with an unregistered email) and `devEmail` names the email that would
 * have been sent.
 */
export const sendLoginCode = async (
  email: string,
  {
    mode = 'login',
    challengeId,
    captchaToken
  }: CodeRequestOptions & { mode?: 'login' | 'register' } = {}
): Promise<{ challengeId: string; devCode: string | null; devEmail: string | null }> => {
  debugAccountCoreCall('sendLoginCode', { email, mode, devMode: isEmailLoginCodeDevMode() })

  const params = new URLSearchParams({ email, mode, delivery: 'code' })
  if (challengeId) params.set('challengeId', challengeId)
  if (isEmailLoginCodeDevMode()) params.set('dev', '1')

  const response = await fetch(`/.netlify/functions/email-login-link-supabase?${params}`, {
    // Header, not query, so the token stays out of URL logs
    headers: captchaToken ? { 'x-turnstile-token': captchaToken } : undefined
  })
  if (!response.ok) {
    await throwForResponse(response, 'We could not send a code. Please try again.')
  }
  const data = (await response.json().catch(() => ({}))) as {
    challengeId?: string
    devCode?: string | null
    devEmail?: string | null
  }
  return {
    challengeId: data.challengeId ?? '',
    devCode: data.devCode ?? null,
    devEmail: data.devEmail ?? null
  }
}

/**
 * Verifies a sign-in code and starts the session. The code is checked by our
 * email-login-code-verify function (per-challenge attempt limits, CAPTCHA when the
 * email is under pressure); only if it's correct does that return a Supabase token
 * hash, which we exchange here exactly like a magic link. supabase-js then stores
 * the session and fires onAuthStateChange, so auth state picks up the user as
 * usual. Accepts "123 456".
 */
export const verifyEmailCode = async (
  email: string,
  code: string,
  {
    challengeId,
    captchaToken,
    beforeSignIn
  }: CodeRequestOptions & {
    /**
     * Called once the code is accepted, just before the session starts — use it to
     * prepare anything that must be ready when auth state flips to signed in.
     */
    beforeSignIn?: (result: { isNewAccount: boolean }) => void
  } = {}
): Promise<{ isNewAccount: boolean }> => {
  const token = code.replace(/\s/g, '')
  debugAccountCoreCall('verifyEmailCode', { email, length: token.length })

  const response = await fetch('/.netlify/functions/email-login-code-verify', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, code: token, challengeId, captchaToken })
  })
  if (!response.ok) {
    debugAccountCoreCall('verifyEmailCode.rejected', { email, status: response.status })
    await throwForResponse(response, 'We could not verify that code. Please try again.')
  }
  const { tokenHash, type, isNewAccount } = (await response.json()) as {
    tokenHash: string
    type?: string
    isNewAccount?: boolean
  }

  const supabase = getSupabaseAuthClient()
  if (!supabase) {
    throw new Error('Supabase auth client is unavailable')
  }
  beforeSignIn?.({ isNewAccount: Boolean(isNewAccount) })
  const { error } = await supabase.auth.verifyOtp({
    token_hash: tokenHash,
    type: (type as 'magiclink' | 'email') || 'magiclink'
  })
  if (error) {
    debugAccountCoreCall('verifyEmailCode.exchangeError', { email, code: error.code })
    throw new Error('We could not finish signing you in. Please request a new code.')
  }

  return { isNewAccount: Boolean(isNewAccount) }
}

export const signOutAuth = async () => {
  debugAccountCoreCall('signOutAuth')
  const supabaseAuth = getSupabaseAccountAuth()

  if (!supabaseAuth) {
    throw new Error('Supabase auth client is unavailable')
  }

  await supabaseAuth.signOut()
  currentAccountAuthUser = null
}

export const deleteCurrentAuthUser = async () => {
  debugAccountCoreCall('deleteCurrentAuthUser')
  await signOutAuth()
}

// Mirrors the account_notification_preferences table defaults: every channel off,
// every type on.
const defaultNotificationPreferences: AccountNotificationPreferences = {
  inAppNotificationsEnabled: false,
  pushNotificationsEnabled: false,
  emailNotificationsEnabled: false,
  notifyUserFollowedYou: true,
  notifyFriendTookAction: true,
  notifyNewActionMatchingInterests: true,
  notifyNewActionInYourArea: true,
  notifyActionPotentialCredited: true,
  allowActionTakenNotifications: true,
  allowActionFavoritedNotifications: true
}

const normalizeVerificationStatus = (
  status?: string | null
): AccountVerificationSummary['status'] => {
  switch (status) {
    case 'pending':
    case 'verified':
    case 'failed':
    case 'canceled':
      return status
    default:
      return 'unverified'
  }
}

const toIsoString = (value?: { toDate?: () => Date } | Date | null) => {
  if (value == null) {
    return null
  }

  if (value instanceof Date) {
    return value.toISOString()
  }

  if (typeof value.toDate === 'function') {
    return value.toDate().toISOString()
  }

  return null
}

const mapPublicProfileFields = (account: ActionToolAccountWithId): SelfAccount['profilePublic'] => ({
  displayName: account.user?.profile?.public?.name ?? null,
  avatarUrl: account.user?.profile?.public?.avatar ?? null,
  bio: account.user?.profile?.private?.bio ?? null,
  occupation: account.user?.profile?.private?.occupation ?? null,
  website: account.user?.profile?.private?.website ?? null,
  twitter: account.user?.profile?.private?.twitter ?? null,
  linkedin: account.user?.profile?.private?.linkedin ?? null,
  threads: account.user?.profile?.private?.threads ?? null,
  bluesky: account.user?.profile?.private?.bluesky ?? null,
  organization: null,
  timezone: null,
  isVerified: account?.stripe_verification?.status === 'verified'
})

const mapPrivateProfileFields = (account: ActionToolAccountWithId): SelfAccount['profilePrivate'] => ({
  imageUrl: account.user?.profile?.private?.image ?? null,
  pronouns: account.user?.profile?.private?.pronouns ?? null,
  locationText: account.user?.profile?.private?.location ?? null,
  locationData: account.user?.profile?.private?.location_data
    ? {
        country: account.user.profile.private.location_data.country ?? null,
        state: account.user.profile.private.location_data.state ?? null,
        city: account.user.profile.private.location_data.city ?? null,
        zipcode: account.user.profile.private.location_data.zipcode ?? null
      }
    : null,
  verbs: account.user?.profile?.private?.verbs ?? [],
  nouns: account.user?.profile?.private?.nouns ?? [],
  skills: account.user?.profile?.private?.skills ?? [],
  cobenefits: account.user?.profile?.private?.cobenefits ?? []
})

const mapAppPreferences = (account: ActionToolAccountWithId): SelfAccount['appPreferences'] => ({
  darkMode: Boolean(account.user?.settings?.dark_mode),
  weeklyReminders: Boolean(account.user?.settings?.weekly_reminders),
  subscribedToIni: Boolean(account.user?.settings?.subscribed_to_ini)
})

const mapPrivacySettings = (account: ActionToolAccountWithId): SelfAccount['privacySettings'] => ({
  isPrivate: Boolean(account.user?.settings?.is_private),
  hidePronouns: Boolean(account.user?.settings?.privacy?.hide_pronouns),
  hideLocation: Boolean(account.user?.settings?.privacy?.hide_location),
  hideVerbs: Boolean(account.user?.settings?.privacy?.hide_verbs),
  hideNouns: Boolean(account.user?.settings?.privacy?.hide_nouns),
  hideCobenefits: Boolean(account.user?.settings?.privacy?.hide_cobenefits),
  hideSkills: Boolean(account.user?.settings?.privacy?.hide_skills),
  hideHistory: Boolean(account.user?.settings?.privacy?.hide_history),
  hideTodo: Boolean(account.user?.settings?.privacy?.hide_todo),
  hideFromAnonymous: Boolean(account.user?.settings?.privacy?.hide_from_anonymous)
})

const mapNotificationPreferences = (
  account: ActionToolAccountWithId
): SelfAccount['notificationPreferences'] => {
  const notificationSettings =
    (account.user?.settings as UserSettings & {
      notifications?: Partial<AccountNotificationPreferences>
    })?.notifications ?? {}

  return {
    ...defaultNotificationPreferences,
    ...notificationSettings
  }
}

const mapProgression = (account: ActionToolAccountWithId): SelfAccount['progression'] => ({
  onboardingLatestStepReached: account.user_progression?.onboarding_latest_step_reached ?? null,
  shouldSubscribe: account.user_progression?.should_subscribe ?? null,
  trialExpiredPopupShownAt:
    account.user_progression?.trial_expired_popup_shown?.map(value => toIsoString(value)).filter(Boolean) ??
    [],
  verificationPromptDismissedAt: toIsoString(account.stripe_verification?.modal_date),
  verificationPublicIntent: account.stripe_verification?.public_intent ?? null
})

const mapBillingSummary = (account: ActionToolAccountWithId): AccountBillingSummary => ({
  premiumAccess: Boolean(account.premium_access),
  hasPaymentMethod: Boolean(account.stripe_customer_id),
  billingProvider: account.stripe_customer_id ? 'stripe' : null,
  stripeCustomerId: account.stripe_customer_id ?? null,
  stripeSubscriptionId: account.stripe_subscription_id ?? null,
  subscriptionStatus: account.premium_access ? 'active' : null,
  trialEndsAt: null,
  subscriptionEndsAt: null,
  customerPortalUrl: null
})

const mapVerificationSummary = (
  account: ActionToolAccountWithId
): AccountVerificationSummary | null => {
  if (!account.stripe_verification) {
    return null
  }

  return {
    status: normalizeVerificationStatus(account.stripe_verification.status),
    publicIntent: account.stripe_verification.public_intent ?? null,
    verifiedName: account.stripe_verification.verified_fields?.name ?? null,
    lastUpdatedAt: toIsoString(account.stripe_verification.modal_date),
    activeVerificationUrl: null
  }
}

export const mapAccountToSelfAccount = (account: ActionToolAccountWithId): SelfAccount => {
  debugAccountCoreCall('mapAccountToSelfAccount', {
    accountId: account.id,
    handle: account.user_id
  })

  return {
    id: account.id,
    handle: account.user_id,
    user_id: account.user_id,
    legacyFirebaseUid: account.id,
    email: account.email ?? null,
    premiumAccess: Boolean(account.premium_access),
    profilePublic: mapPublicProfileFields(account),
    profilePrivate: mapPrivateProfileFields(account),
    appPreferences: mapAppPreferences(account),
    privacySettings: mapPrivacySettings(account),
    notificationPreferences: mapNotificationPreferences(account),
    progression: mapProgression(account),
    roleSummary: {
      roles: [],
      partnerStatus: null
    },
    billing: mapBillingSummary(account),
    verification: mapVerificationSummary(account),
    locations: account.user?.locations ?? []
  }
}

export const mapSelfAccountToLegacyAccount = (
  account: SelfAccount,
  supplementalActivity?: PublicProfileActivity | null
): ActionToolAccountWithId => ({
    id: account.id,
    unique_id: account.id,
    user_id: account.handle,
    email: account.email ?? '',
    premium_access: Boolean(account.premiumAccess),
    beehiiv_id: null,
    stripe_customer_id: account.billing?.stripeCustomerId ?? null,
    stripe_subscription_id: account.billing?.stripeSubscriptionId ?? null,
    stripe_verification:
      account.verification ||
      account.progression.verificationPromptDismissedAt ||
      account.progression.verificationPublicIntent !== null
        ? {
            session_id: account.verification?.activeVerificationUrl ?? null,
            status: account.verification?.status ?? 'unverified',
            public_intent:
              account.verification?.publicIntent ??
              account.progression.verificationPublicIntent ??
              undefined,
            modal_date: account.progression.verificationPromptDismissedAt
              ? createLegacyTimestamp(account.progression.verificationPromptDismissedAt)
              : undefined,
            verified_fields: {
              name: account.verification?.verifiedName ?? null
            }
          }
        : null,
    user_progression: {
      onboarding_latest_step_reached: account.progression.onboardingLatestStepReached ?? undefined,
      should_subscribe: account.progression.shouldSubscribe ?? undefined,
      trial_expired_popup_shown: []
    },
    user: {
      action_history:
        supplementalActivity?.actionHistory.map(action => ({
          action_slug: action.actionSlug,
          action_id: action.actionId ?? undefined,
          user_action_id: action.userActionId ?? undefined,
          date_completed: action.completedAt ? new Date(action.completedAt) : undefined,
          meta_data:
            action.title || action.externalUrl || action.metaType
              ? {
                  title: action.title ?? undefined,
                  external_url: action.externalUrl ?? undefined,
                  meta_type: action.metaType ?? undefined,
                  type: action.metaType === 'give-lively-donation' ? 'donation' : undefined
                }
              : undefined
        })) ?? [],
      saved_actions: supplementalActivity?.savedActionSlugs ?? [],
      friends: [],
      locations: account.locations ?? [],
      profile: {
        public: {
          name: account.profilePublic.displayName ?? undefined,
          avatar: account.profilePublic.avatarUrl ?? undefined
        },
        private: {
          image: account.profilePrivate.imageUrl ?? undefined,
          pronouns: account.profilePrivate.pronouns ?? undefined,
          location: account.profilePrivate.locationText ?? undefined,
          location_data: account.profilePrivate.locationData
            ? {
                country: account.profilePrivate.locationData.country ?? undefined,
                state: account.profilePrivate.locationData.state ?? undefined,
                city: account.profilePrivate.locationData.city ?? undefined,
                zipcode: account.profilePrivate.locationData.zipcode ?? undefined
              }
            : undefined,
          verbs: account.profilePrivate.verbs,
          nouns: account.profilePrivate.nouns,
          skills: account.profilePrivate.skills,
          cobenefits: account.profilePrivate.cobenefits,
          bio: account.profilePublic.bio ?? undefined,
          occupation: account.profilePublic.occupation ?? undefined,
          website: account.profilePublic.website ?? undefined,
          twitter: account.profilePublic.twitter ?? undefined,
          linkedin: account.profilePublic.linkedin ?? undefined,
          threads: account.profilePublic.threads ?? undefined,
          bluesky: account.profilePublic.bluesky ?? undefined
        }
      },
      settings: {
        is_private: account.privacySettings.isPrivate,
        dark_mode: account.appPreferences.darkMode,
        weekly_reminders: account.appPreferences.weeklyReminders,
        subscribed_to_ini: account.appPreferences.subscribedToIni,
        privacy: {
          hide_pronouns: account.privacySettings.hidePronouns,
          hide_location: account.privacySettings.hideLocation,
          hide_verbs: account.privacySettings.hideVerbs,
          hide_nouns: account.privacySettings.hideNouns,
          hide_cobenefits: account.privacySettings.hideCobenefits,
          hide_skills: account.privacySettings.hideSkills,
          hide_history: account.privacySettings.hideHistory,
          hide_todo: account.privacySettings.hideTodo,
          hide_from_anonymous: account.privacySettings.hideFromAnonymous
        },
        // Notification preferences (camelCase on SelfAccount) surfaced in the
        // snake_case shape the notification UI reads. Without this the UI never
        // sees stored values and defaults every toggle to "on".
        notifications: {
          in_app_notifications_enabled: account.notificationPreferences.inAppNotificationsEnabled,
          push_notifications_enabled: account.notificationPreferences.pushNotificationsEnabled,
          email_notifications_enabled: account.notificationPreferences.emailNotificationsEnabled,
          notify_user_followed_you: account.notificationPreferences.notifyUserFollowedYou,
          notify_friend_took_action: account.notificationPreferences.notifyFriendTookAction,
          notify_new_action_matching_interests:
            account.notificationPreferences.notifyNewActionMatchingInterests,
          notify_new_action_in_your_area: account.notificationPreferences.notifyNewActionInYourArea,
          notify_action_potential_credited:
            account.notificationPreferences.notifyActionPotentialCredited
        },
        notification_privacy: {
          allow_action_taken_notifications:
            account.notificationPreferences.allowActionTakenNotifications,
          allow_action_favorited_notifications:
            account.notificationPreferences.allowActionFavoritedNotifications
        }
      }
    }
  })

const createOwnPublicProfile = (
  account: ActionToolAccountWithId,
  followerCount: number,
  followingCount: number
): PublicProfile => {
  const selfAccount = mapAccountToSelfAccount(account)

  return {
    id: selfAccount.id,
    handle: selfAccount.handle,
    user_id: selfAccount.user_id,
    followerCount,
    followingCount,
    profile: {
      ...selfAccount.profilePublic,
      pronouns: selfAccount.profilePrivate.pronouns,
      locationText: selfAccount.profilePrivate.locationText,
      locationData: selfAccount.profilePrivate.locationData,
      verbs: selfAccount.profilePrivate.verbs,
      nouns: selfAccount.profilePrivate.nouns,
      skills: selfAccount.profilePrivate.skills,
      cobenefits: selfAccount.profilePrivate.cobenefits
    }
  }
}

export const mapAccountToPublicProfile = (
  account: ActionToolAccountWithId,
  {
    followerCount = 0,
    followingCount = account.user?.friends?.length ?? 0,
    viewerAccountId
  }: {
    followerCount?: number
    followingCount?: number
    viewerAccountId?: string | null
  } = {}
): PublicProfile | null => {
  debugAccountCoreCall('mapAccountToPublicProfile', {
    accountId: account.id,
    handle: account.user_id,
    followerCount,
    followingCount,
    isViewerSubject: viewerAccountId === account.id
  })

  if (viewerAccountId && viewerAccountId === account.id) {
    return createOwnPublicProfile(account, followerCount, followingCount)
  }

  return applyPrivacySettingsToProfile({
    id: account.id,
    handle: account.user_id,
    followerCount,
    followingCount,
    publicProfile: mapPublicProfileFields(account),
    privateProfile: mapPrivateProfileFields(account),
    settings: mapPrivacySettings(account)
  })
}

export const mapUserToPublicProfileCard = (
  user: ActionToolUserWithId
): PublicProfileCard => {
  debugAccountCoreCall('mapUserToPublicProfileCard', {
    accountId: user.id,
    handle: user.user_id
  })

  return {
    id: user.id,
    handle: user.user_id,
    user_id: user.user_id,
    profile: {
      displayName: user.profile?.public?.name ?? null,
      avatarUrl: user.profile?.public?.avatar ?? null,
      isVerified: Boolean(user.is_verified),
      locationText: user.profile?.private?.location ?? null,
      verbs: user.profile?.private?.verbs ?? [],
      nouns: user.profile?.private?.nouns ?? [],
      skills: user.profile?.private?.skills ?? [],
      cobenefits: user.profile?.private?.cobenefits ?? []
    }
  }
}

export const mapPublicProfileToPublicProfileCard = (
  profile: PublicProfile
): PublicProfileCard => ({
  id: profile.id,
  handle: profile.handle,
  user_id: profile.user_id,
  profile: {
    displayName: profile.profile.displayName,
    avatarUrl: profile.profile.avatarUrl,
    isVerified: profile.profile.isVerified,
    locationText: profile.profile.locationText,
    verbs: profile.profile.verbs ?? [],
    nouns: profile.profile.nouns ?? [],
    skills: profile.profile.skills ?? [],
    cobenefits: profile.profile.cobenefits ?? []
  }
})

export const mapPublicProfileCardToLegacyUser = (
  profileCard: PublicProfileCard
): ActionToolUserWithId => {
  debugAccountCoreCall('mapPublicProfileCardToLegacyUser', {
    accountId: profileCard.id,
    handle: profileCard.handle
  })

  return {
    id: profileCard.id,
    user_id: profileCard.user_id,
    is_verified: Boolean(profileCard.profile.isVerified),
    friends: [],
    profile: {
      public: {
        name: profileCard.profile.displayName ?? undefined,
        avatar: profileCard.profile.avatarUrl ?? undefined
      },
      private: {
        location: profileCard.profile.locationText ?? undefined,
        verbs: profileCard.profile.verbs,
        nouns: profileCard.profile.nouns,
        skills: profileCard.profile.skills,
        cobenefits: profileCard.profile.cobenefits
      }
    },
    settings: {
      is_private: false,
      privacy: {
        hide_location: profileCard.profile.locationText == null,
        hide_verbs: profileCard.profile.verbs.length === 0,
        hide_nouns: profileCard.profile.nouns.length === 0,
        hide_cobenefits: profileCard.profile.cobenefits.length === 0,
        hide_skills: profileCard.profile.skills.length === 0
      }
    }
  }
}

export const mapPublicProfileToLegacyUser = (
  profile: PublicProfile,
  supplementalActivity?: PublicProfileActivity | null
): ActionToolUserWithId => {
  const privacyOverrides = supplementalActivity
    ? {
        hide_history: supplementalActivity.hideHistory,
        hide_todo: supplementalActivity.hideTodo
      }
    : {}

  return {
    id: profile.id,
    user_id: profile.user_id,
    is_verified: profile.profile.isVerified,
    action_history:
      supplementalActivity?.actionHistory.map(action => ({
        action_slug: action.actionSlug,
        action_id: action.actionId ?? undefined,
        user_action_id: action.userActionId ?? undefined
      })) ?? [],
    saved_actions: supplementalActivity?.savedActionSlugs ?? [],
    friends: [],
    profile: {
      public: {
        name: profile.profile.displayName ?? undefined,
        avatar: profile.profile.avatarUrl ?? undefined
      },
      private: {
        pronouns: profile.profile.pronouns ?? undefined,
        location: profile.profile.locationText ?? undefined,
        location_data: profile.profile.locationData
          ? {
              country: profile.profile.locationData.country ?? undefined,
              state: profile.profile.locationData.state ?? undefined,
              city: profile.profile.locationData.city ?? undefined,
              zipcode: profile.profile.locationData.zipcode ?? undefined
            }
          : undefined,
        verbs: profile.profile.verbs ?? [],
        nouns: profile.profile.nouns ?? [],
        skills: profile.profile.skills ?? [],
        cobenefits: profile.profile.cobenefits ?? [],
        bio: profile.profile.bio ?? undefined,
        occupation: profile.profile.occupation ?? undefined,
        website: profile.profile.website ?? undefined,
        twitter: profile.profile.twitter ?? undefined,
        linkedin: profile.profile.linkedin ?? undefined,
        threads: profile.profile.threads ?? undefined,
        bluesky: profile.profile.bluesky ?? undefined
      }
    },
    settings: {
      is_private: false,
      privacy: {
        hide_pronouns: profile.profile.pronouns == null,
        hide_location: profile.profile.locationText == null,
        hide_verbs: (profile.profile.verbs?.length ?? 0) === 0,
        hide_nouns: (profile.profile.nouns?.length ?? 0) === 0,
        hide_cobenefits: (profile.profile.cobenefits?.length ?? 0) === 0,
        hide_skills: (profile.profile.skills?.length ?? 0) === 0,
        ...privacyOverrides
      }
    }
  }
}

export const mapPublicProfileToLegacyAccount = (
  profile: PublicProfile,
  supplementalAccount?: ActionToolAccountWithId | null
): ActionToolAccountWithId => {
  const supplementalUser = supplementalAccount?.user
  const supplementalPrivacy = supplementalUser?.settings?.privacy
  const mappedUser = mapPublicProfileToLegacyUser(profile, {
    actionHistory: (supplementalUser?.action_history ?? []).map(action => ({
      actionSlug: action.action_slug,
      actionId: action.action_id ?? null,
      userActionId: action.user_action_id ?? null,
      completedAt: action.date_completed ? action.date_completed.toISOString() : null,
      title: action.meta_data?.title ?? null,
      externalUrl: action.meta_data?.external_url ?? null,
      metaType: action.meta_data?.meta_type ?? null
    })),
    savedActionSlugs: supplementalUser?.saved_actions ?? [],
    hideHistory: supplementalPrivacy?.hide_history ?? true,
    hideTodo: supplementalPrivacy?.hide_todo ?? true,
    isComplete: true
  })

  return {
    id: profile.id,
    unique_id: supplementalAccount?.unique_id ?? profile.id,
    user_id: profile.user_id,
    email: supplementalAccount?.email ?? '',
    // Other accounts' premium status is owner-only; nothing renders it for them.
    premium_access: false,
    beehiiv_id: supplementalAccount?.beehiiv_id ?? null,
    stripe_customer_id: supplementalAccount?.stripe_customer_id ?? null,
    stripe_subscription_id: supplementalAccount?.stripe_subscription_id ?? null,
    stripe_verification: profile.profile.isVerified
      ? {
          session_id: supplementalAccount?.stripe_verification?.session_id ?? null,
          status: 'verified',
          public_intent: supplementalAccount?.stripe_verification?.public_intent ?? true,
          modal_date: supplementalAccount?.stripe_verification?.modal_date,
          verified_fields: {
            name: supplementalAccount?.stripe_verification?.verified_fields?.name ?? null
          }
        }
      : supplementalAccount?.stripe_verification ?? null,
    user_progression: supplementalAccount?.user_progression ?? {},
    user: {
      action_history: mappedUser.action_history,
      saved_actions: mappedUser.saved_actions,
      friends: supplementalUser?.friends ?? [],
      profile: mappedUser.profile,
      settings: {
        ...mappedUser.settings,
        is_private: supplementalUser?.settings?.is_private ?? false,
        dark_mode: supplementalUser?.settings?.dark_mode,
        weekly_reminders: supplementalUser?.settings?.weekly_reminders,
        subscribed_to_ini: supplementalUser?.settings?.subscribed_to_ini
      }
    }
  }
}

export const getAccountsWithUserId = async (userId: string) => {
  debugAccountCoreCall('getAccountsWithUserId', { userId })
  const account = await getPublicAccountByHandle(userId)
  return account ? [account] : []
}

export const getAccountsById = async (ids: string[]) => {
  debugAccountCoreCall('getAccountsById', { count: ids.length })
  const service = getSupabaseAccountService()
  const profiles = service
    ? await service.getPublicProfilesByIds(ids, currentAccountAuthUser?.uid)
    : []
  return profiles.map(mapPublicProfileCardToLegacyUser)
}

export const finalizePremiumPurchase = async ({
  accountId,
  customerId,
  subscriptionId
}: {
  accountId: string
  customerId: string
  subscriptionId: string
}) => {
  debugAccountCoreCall('finalizePremiumPurchase', {
    accountId,
    hasCustomerId: Boolean(customerId),
    hasSubscriptionId: Boolean(subscriptionId)
  })

  await postSupabaseAccountAdminRequest(
    '/.netlify/functions/account-supabase-finalize-premium-purchase',
    {
      accountId,
      customerId,
      subscriptionId
    }
  )

  return await getAccount(accountId)
}

// Uploads the signed-in user's avatar (a data URL) to Supabase Storage and
// returns its public URL. The endpoint finds the account from the session token.
export const uploadAccountAvatar = async (file: string) => {
  debugAccountCoreCall('uploadAccountAvatar', { bytes: file.length })

  const result = await postSupabaseAccountAdminRequest<{ url: string }>(
    '/.netlify/functions/account-avatar-upload',
    { file }
  )
  if (!result?.url) {
    throw new Error('Could not upload your avatar')
  }
  return result.url
}

// Cancels the signed-in user's own identity verification (the endpoint finds the
// account and session from the session token) and returns the reloaded account.
export const cancelAccountVerification = async (accountId: string) => {
  debugAccountCoreCall('cancelAccountVerification', { accountId })

  await postSupabaseAccountAdminRequest('/.netlify/functions/stripe-cancel-verification-session', {})

  return await getAccount(accountId)
}

export const ensureStripeCustomerForAccount = async ({
  accountId,
  email
}: {
  accountId: string
  email: string
}) => {
  debugAccountCoreCall('ensureStripeCustomerForAccount', {
    accountId,
    hasEmail: Boolean(email)
  })

  const result = await postSupabaseAccountAdminRequest<{ customerId: string }>(
    '/.netlify/functions/account-supabase-ensure-stripe-customer',
    {
      accountId,
      email
    }
  )

  return {
    account: await getAccount(accountId),
    customerId: result?.customerId ?? null
  }
}

export const deleteAccountRecord = async (accountId: string) => {
  debugAccountCoreCall('deleteAccountRecord', { accountId })
  await postSupabaseAccountAdminRequest('/.netlify/functions/account-supabase-delete', {
    accountId,
    authUserId: currentAccountAuthUser?.authUserId ?? null
  })
  currentAccountAuthUser = null
  return true
}

export const getAccount = async (accountId: string): Promise<ActionToolAccountWithId | false> => {
  debugAccountCoreCall('getAccount', { accountId })
  const session = await ensureSupabaseAuthSessionReady()

  if (!session) {
    debugAccountCoreCall('supabase.getAccount.skippedWithoutSession', { accountId })
    return false
  }

  const service = getSupabaseAccountService()
  const [selfAccount, following] = service
    ? await Promise.all([
        service.getSelfAccount(accountId),
        service.listFollowing(accountId, accountId)
      ])
    : [null, []]
  if (!selfAccount) {
    return false
  }

  const activity = service
    ? await service.getPublicProfileActivity(selfAccount.handle, selfAccount.id)
    : null

  const compatAccount = mapSelfAccountToLegacyAccount(selfAccount, activity)
  if (compatAccount.user) {
    compatAccount.user.friends = following.map(profile => profile.id)
  }

  return compatAccount
}

export const waitForAccountInitialization = async (
  accountId: string,
  {
    attempts = 8,
    delayMs = 250
  }: {
    attempts?: number
    delayMs?: number
  } = {}
): Promise<ActionToolAccountWithId | false> => {
  debugAccountCoreCall('waitForAccountInitialization', {
    accountId,
    attempts,
    delayMs
  })

  const session = await ensureSupabaseAuthSessionReady()

  if (!session) {
    debugAccountCoreCall('supabase.waitForAccountInitialization.skippedWithoutSession', {
      accountId
    })
    return false
  }

  const service = getSupabaseAccountService()
  if (!service) {
    return false
  }

  const [selfAccount, following] = await Promise.all([
    waitForSelfAccount({
      accountId,
      attempts,
      delayMs,
      reader: service
    }),
    service.listFollowing(accountId, accountId)
  ])

  if (!selfAccount) {
    return false
  }

  const activity = await service.getPublicProfileActivity(selfAccount.handle, selfAccount.id)
  const compatAccount = mapSelfAccountToLegacyAccount(selfAccount, activity)

  if (compatAccount.user) {
    compatAccount.user.friends = following.map(profile => profile.id)
  }

  return compatAccount
}

export const getPublicAccountByHandle = async (handle: string, viewerAccountId?: string) => {
  debugAccountCoreCall('getPublicAccountByHandle', { handle, viewerAccountId })
  const profile = await getPublicProfile(handle, viewerAccountId || null)
  return profile ? mapPublicProfileToLegacyAccount(profile, undefined) : false
}

export const loadAccountSnapshot = async (
  accountId: string,
  onLoaded?: (account: ActionToolAccountWithId) => void
) => {
  debugAccountCoreCall('loadAccountSnapshot', { accountId })

  const account = await getAccount(accountId)

  if (account) {
    onLoaded?.(account)
  }

  return account
}

export const observeFriendActivity = async (
  accountId: string,
  onChange: (event: FriendActivityRealtimeEvent) => void
) => {
  debugAccountCoreCall('observeFriendActivity', { accountId })

  await ensureSupabaseAuthSessionReady()

  const realtime = getSupabaseAccountRealtimeClient()
  if (!realtime) {
    return () => undefined
  }

  return await realtime.observeFriendActivity(accountId, onChange)
}

export const observeNotifications = async (
  accountId: string,
  onChange: (event: AccountNotificationRealtimeEvent) => void
) => {
  debugAccountCoreCall('observeNotifications', { accountId })

  await ensureSupabaseAuthSessionReady()

  const realtime = getSupabaseAccountRealtimeClient()
  if (!realtime) {
    return () => undefined
  }

  return await realtime.observeNotifications(accountId, onChange)
}

export const isSupabaseAuthActive = () => true

export const updateAccountHandleFromDisplayName = async (
  accountId: string,
  displayName: string
) => {
  debugAccountCoreCall('updateAccountHandleFromDisplayName', {
    accountId,
    hasDisplayName: Boolean(displayName)
  })
  const result = await accountService.updateHandle(accountId, displayName)
  return result.value.user_id
}

const getSupabaseAccountService = (): ExtendedAccountClientService | null => {
  if (process.env.GATSBY_ENABLE_SUPABASE_ACCOUNT_CORE !== 'true') {
    debugAccountCoreCall('supabase.disabled', { area: 'account-core' })
    return null
  }

  const supabase = getSupabaseAuthClient() || getSupabaseClient()
  if (!supabase) {
    return null
  }

  return createAccountClient({
    supabase,
    authContext: {
      email: currentAccountAuthUser?.email ?? null,
      legacyFirebaseUid: currentAccountAuthUser?.uid ?? null
    },
    logger: (event, metadata) => debugAccountCoreCall(`supabase.${event}`, metadata)
  }) as ExtendedAccountClientService
}

const getSupabaseAccountRealtimeClient = () => {
  if (process.env.GATSBY_ENABLE_SUPABASE_ACCOUNT_CORE !== 'true') {
    debugAccountCoreCall('supabase.disabled', { area: 'account-core-realtime' })
    return null
  }

  const supabase = getSupabaseAuthClient() || getSupabaseClient()
  if (!supabase) {
    return null
  }

  return createAccountRealtimeClient({
    supabase,
    logger: (event, metadata) => debugAccountCoreCall(`supabase.${event}`, metadata)
  })
}

const supabaseBackedAccountService: ExtendedAccountService = {
  async getSelfAccount(accountId) {
    debugAccountCoreCall('supabaseBackedAccountService.getSelfAccount', { accountId })
    const service = getSupabaseAccountService()
    return service ? await service.getSelfAccount(accountId) : null
  },
  async getPublicProfile(handle, viewerAccountId) {
    debugAccountCoreCall('supabaseBackedAccountService.getPublicProfile', {
      handle,
      viewerAccountId
    })
    const service = getSupabaseAccountService()
    return service ? await service.getPublicProfile(handle, viewerAccountId) : null
  },
  async getPublicProfilesByIds(accountIds, viewerAccountId) {
    debugAccountCoreCall('supabaseBackedAccountService.getPublicProfilesByIds', {
      count: accountIds.length,
      viewerAccountId
    })
    const service = getSupabaseAccountService()
    return service ? await service.getPublicProfilesByIds(accountIds, viewerAccountId) : []
  },
  async getPublicProfileActivity(handle, viewerAccountId) {
    debugAccountCoreCall('supabaseBackedAccountService.getPublicProfileActivity', {
      handle,
      viewerAccountId
    })
    const service = getSupabaseAccountService()
    const activity = service ? await service.getPublicProfileActivity(handle, viewerAccountId) : null

    debugAccountCoreCall('supabaseBackedAccountService.getPublicProfileActivity.todos', {
      handle,
      viewerAccountId,
      savedActionCount: activity?.savedActionSlugs?.length ?? 0,
      savedActionSlugs: activity?.savedActionSlugs?.join(',') || null
    })

    return activity
  },
  async searchPublicProfiles(filters, viewerAccountId) {
    debugAccountCoreCall('supabaseBackedAccountService.searchPublicProfiles', {
      hasName: Boolean(filters.name),
      viewerAccountId
    })
    const service = getSupabaseAccountService()
    return service ? await service.searchPublicProfiles(filters, viewerAccountId) : []
  },
  async listFollowers(accountId, viewerAccountId) {
    debugAccountCoreCall('supabaseBackedAccountService.listFollowers', {
      accountId,
      viewerAccountId
    })
    const service = getSupabaseAccountService()
    return service ? await service.listFollowers(accountId, viewerAccountId) : []
  },
  async listFollowing(accountId, viewerAccountId) {
    debugAccountCoreCall('supabaseBackedAccountService.listFollowing', {
      accountId,
      viewerAccountId
    })
    const service = getSupabaseAccountService()
    return service ? await service.listFollowing(accountId, viewerAccountId) : []
  },
  async getUserPaymentInfo(accountId) {
    debugAccountCoreCall('supabaseBackedAccountService.getUserPaymentInfo', { accountId })
    const service = getSupabaseAccountService()
    return service ? await service.getUserPaymentInfo(accountId) : null
  },
  async getVerificationSummary(accountId) {
    debugAccountCoreCall('supabaseBackedAccountService.getVerificationSummary', { accountId })
    const service = getSupabaseAccountService()
    return service ? await service.getVerificationSummary(accountId) : null
  },
  async updateHandle(accountId, handle) {
    debugAccountCoreCall('supabaseBackedAccountService.updateHandle', {
      accountId,
      hasHandle: Boolean(handle)
    })
    const service = getSupabaseAccountService()
    if (!service) {
      throw new Error('Supabase account client is not available')
    }
    return await service.updateHandle(accountId, handle)
  },
  async updateProfile(accountId, input) {
    debugAccountCoreCall('supabaseBackedAccountService.updateProfile', { accountId })
    const service = getSupabaseAccountService()
    if (!service) {
      throw new Error('Supabase account client is not available')
    }
    return await service.updateProfile(accountId, input)
  },
  async listAccountLocations(accountId) {
    debugAccountCoreCall('supabaseBackedAccountService.listAccountLocations', { accountId })
    const service = getSupabaseAccountService()
    return service ? await service.listAccountLocations(accountId) : []
  },
  async upsertAccountLocation(accountId, input) {
    debugAccountCoreCall('supabaseBackedAccountService.upsertAccountLocation', {
      accountId,
      isUpdate: Boolean(input.id)
    })
    const service = getSupabaseAccountService()
    if (!service) {
      throw new Error('Supabase account client is not available')
    }
    return await service.upsertAccountLocation(accountId, input)
  },
  async setHomeAccountLocation(accountId, locationId, confirmed) {
    debugAccountCoreCall('supabaseBackedAccountService.setHomeAccountLocation', {
      accountId,
      locationId
    })
    const service = getSupabaseAccountService()
    if (!service) {
      throw new Error('Supabase account client is not available')
    }
    return await service.setHomeAccountLocation(accountId, locationId, confirmed)
  },
  async deleteAccountLocation(accountId, locationId) {
    debugAccountCoreCall('supabaseBackedAccountService.deleteAccountLocation', {
      accountId,
      locationId
    })
    const service = getSupabaseAccountService()
    if (!service) {
      throw new Error('Supabase account client is not available')
    }
    return await service.deleteAccountLocation(accountId, locationId)
  },
  async updatePrivacySettings(accountId, input) {
    debugAccountCoreCall('supabaseBackedAccountService.updatePrivacySettings', { accountId })
    const service = getSupabaseAccountService()
    if (!service) {
      throw new Error('Supabase account client is not available')
    }
    return await service.updatePrivacySettings(accountId, input)
  },
  async updateAppPreferences(accountId, input) {
    debugAccountCoreCall('supabaseBackedAccountService.updateAppPreferences', { accountId })
    const service = getSupabaseAccountService()
    if (!service) {
      throw new Error('Supabase account client is not available')
    }
    return await service.updateAppPreferences(accountId, input)
  },
  async updateNotificationPreferences(accountId, input) {
    debugAccountCoreCall('supabaseBackedAccountService.updateNotificationPreferences', { accountId })
    const service = getSupabaseAccountService()
    if (!service) {
      throw new Error('Supabase account client is not available')
    }
    return await service.updateNotificationPreferences(accountId, input)
  },
  async updateProgression(accountId, input) {
    debugAccountCoreCall('supabaseBackedAccountService.updateProgression', { accountId })
    const service = getSupabaseAccountService()
    if (!service) {
      throw new Error('Supabase account client is not available')
    }
    return await service.updateProgression(accountId, input)
  },
  async saveAction(accountId, actionSlug) {
    debugAccountCoreCall('supabaseBackedAccountService.saveAction', { accountId, actionSlug })
    const service = getSupabaseAccountService()
    if (!service) {
      throw new Error('Supabase account client is not available')
    }
    return await service.saveAction(accountId, actionSlug)
  },
  async unsaveAction(accountId, actionSlug) {
    debugAccountCoreCall('supabaseBackedAccountService.unsaveAction', { accountId, actionSlug })
    const service = getSupabaseAccountService()
    if (!service) {
      throw new Error('Supabase account client is not available')
    }
    return await service.unsaveAction(accountId, actionSlug)
  },
  async takeAction(accountId, input) {
    debugAccountCoreCall('supabaseBackedAccountService.takeAction', {
      accountId,
      actionSlug: input.actionSlug,
      actionId: input.actionId ?? null
    })
    const service = getSupabaseAccountService()
    if (!service) {
      throw new Error('Supabase account client is not available')
    }
    return await service.takeAction(accountId, input)
  },
  async removeAction(accountId, input) {
    debugAccountCoreCall('supabaseBackedAccountService.removeAction', {
      accountId,
      actionSlug: input.actionSlug,
      actionId: input.actionId ?? null
    })
    const service = getSupabaseAccountService()
    if (!service) {
      throw new Error('Supabase account client is not available')
    }
    return await service.removeAction(accountId, input)
  },
  async followAccount(accountId, targetAccountId) {
    debugAccountCoreCall('supabaseBackedAccountService.followAccount', {
      accountId,
      targetAccountId
    })
    const service = getSupabaseAccountService()
    if (!service) {
      throw new Error('Supabase account client is not available')
    }
    return await service.followAccount(accountId, targetAccountId)
  },
  async unfollowAccount(accountId, targetAccountId) {
    debugAccountCoreCall('supabaseBackedAccountService.unfollowAccount', {
      accountId,
      targetAccountId
    })
    const service = getSupabaseAccountService()
    if (!service) {
      throw new Error('Supabase account client is not available')
    }
    return await service.unfollowAccount(accountId, targetAccountId)
  }
}

export const accountService: ExtendedAccountService = supabaseBackedAccountService

export const getSelfAccount = (accountId: string) => accountService.getSelfAccount(accountId)
export const getPublicProfile = (handle: string, viewerAccountId?: string | null) =>
  accountService.getPublicProfile(handle, viewerAccountId)
export const getPublicProfileActivity = (handle: string, viewerAccountId?: string | null) =>
  accountService.getPublicProfileActivity(handle, viewerAccountId)
export const searchPublicProfiles = (
  filters: Parameters<AccountService['searchPublicProfiles']>[0],
  viewerAccountId?: string | null
) => accountService.searchPublicProfiles(filters, viewerAccountId)
export const listFollowers = (accountId: string, viewerAccountId?: string | null) =>
  accountService.listFollowers(accountId, viewerAccountId)
export const listFollowing = (accountId: string, viewerAccountId?: string | null) =>
  accountService.listFollowing(accountId, viewerAccountId)
export const getUserPaymentInfo = (accountId: string) => accountService.getUserPaymentInfo(accountId)
export const getVerificationSummary = (accountId: string) =>
  accountService.getVerificationSummary(accountId)
export const updateHandle = (accountId: string, handle: string) =>
  accountService.updateHandle(accountId, handle)
export const updateProfile = (
  accountId: string,
  input: Parameters<AccountService['updateProfile']>[1]
) => accountService.updateProfile(accountId, input)
export const updatePrivacySettings = (
  accountId: string,
  input: Parameters<AccountService['updatePrivacySettings']>[1]
) => accountService.updatePrivacySettings(accountId, input)
export const listAccountLocations = (accountId: string) =>
  accountService.listAccountLocations(accountId)
export const upsertAccountLocation = (
  accountId: string,
  input: Parameters<AccountService['upsertAccountLocation']>[1]
) => accountService.upsertAccountLocation(accountId, input)
export const setHomeAccountLocation = (accountId: string, locationId: string, confirmed?: boolean) =>
  accountService.setHomeAccountLocation(accountId, locationId, confirmed)
export const deleteAccountLocation = (accountId: string, locationId: string) =>
  accountService.deleteAccountLocation(accountId, locationId)
export const updateAppPreferences = (
  accountId: string,
  input: Parameters<AccountService['updateAppPreferences']>[1]
) => accountService.updateAppPreferences(accountId, input)
export const updateNotificationPreferences = (
  accountId: string,
  input: Parameters<AccountService['updateNotificationPreferences']>[1]
) => accountService.updateNotificationPreferences(accountId, input)
export const updateProgression = (
  accountId: string,
  input: Parameters<AccountService['updateProgression']>[1]
) => accountService.updateProgression(accountId, input)
export const saveAction = (accountId: string, actionSlug: string) =>
  accountService.saveAction(accountId, actionSlug)
export const unsaveAction = (accountId: string, actionSlug: string) =>
  accountService.unsaveAction(accountId, actionSlug)
export const takeAction = (
  accountId: string,
  input: Parameters<AccountService['takeAction']>[1]
) => accountService.takeAction(accountId, input)
export const removeAction = (
  accountId: string,
  input: Parameters<AccountService['removeAction']>[1]
) => accountService.removeAction(accountId, input)
export const followAccount = (accountId: string, targetAccountId: string) =>
  accountService.followAccount(accountId, targetAccountId)
export const unfollowAccount = (accountId: string, targetAccountId: string) =>
  accountService.unfollowAccount(accountId, targetAccountId)
